<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import BarChart from "shared/components/charts/BarChart.vue";
import ReportHeader from "./components/ReportHeader.vue";
import FilterSelector from "./components/FilterSelector.vue";
import LoadingState from "dashboard/components/widgets/LoadingState.vue";

const store = useStore();
const { t } = useI18n();

// ── Current user / role ────────────────────────────────────────────
const currentUser = computed(() => store.getters.getCurrentUser);
const isAdmin = computed(() => currentUser.value?.role === "administrator");

// ── Filters ────────────────────────────────────────────────────────
const from = ref(0);
const to = ref(0);
const datePreset = ref(null); // current_week | current_month | null (rangos por offset)
const selectedAgent = ref(null); // null = Todos los agentes
const selectedTeam = ref(null); // null = Todos los equipos
const selectedCurrency = ref("ARS");
const selectedGranularity = ref("day");
const selectedLabel = ref(null); // null = Todas las etiquetas (FORK)
const selectedLabelOperator = ref("contains"); // contains | does_not_contain (FORK)

const granularityOptions = computed(() => [
  { id: "day", label: t("CONVERSION_FUNNEL.FILTERS.GRANULARITY_DAY") },
  { id: "week", label: t("CONVERSION_FUNNEL.FILTERS.GRANULARITY_WEEK") },
  { id: "month", label: t("CONVERSION_FUNNEL.FILTERS.GRANULARITY_MONTH") },
  { id: "quarter", label: t("CONVERSION_FUNNEL.FILTERS.GRANULARITY_QUARTER") },
]);

const currencyOptions = [
  { id: "ARS", label: "ARS" },
  { id: "USD", label: "USD" },
];

// ── Agents ─────────────────────────────────────────────────────────
const agentsOptions = computed(() => store.getters["agents/getAgents"] || []);

const agentOptionsWithAll = computed(() => [
  { id: null, name: t("CONVERSION_FUNNEL.FILTERS.ALL_AGENTS") },
  ...agentsOptions.value,
]);

// ── Labels (FORK) ──────────────────────────────────────────────────
// Todas las etiquetas (cualquier scope). El backend matchea por unión entre
// las relaciones que cada etiqueta habilita (deal directo / conversación / contacto).
const labelOptions = computed(() => store.getters["labels/getLabels"] || []);

const labelOptionsWithAll = computed(() => [
  { id: null, title: t("CONVERSION_FUNNEL.FILTERS.ALL_LABELS") },
  ...labelOptions.value,
]);

const labelOperatorOptions = computed(() => [
  {
    id: "contains",
    label: t("CONVERSION_FUNNEL.FILTERS.LABEL_OPERATOR_CONTAINS"),
  },
  {
    id: "does_not_contain",
    label: t("CONVERSION_FUNNEL.FILTERS.LABEL_OPERATOR_NOT_CONTAINS"),
  },
]);

// Nota: cuando la etiqueta no toca contactos ni conversaciones (solo deal), los
// leads quedan sin filtrar. Mostramos un aviso para que no confunda los KPIs de leads.
const showLabelDealNote = computed(() => {
  const types = selectedLabel.value?.labelable_types || [];
  return (
    Boolean(selectedLabel.value?.id) &&
    !types.includes("contact") &&
    !types.includes("conversation")
  );
});

// ── Teams ──────────────────────────────────────────────────────────
// Admins ven todos los teams; agentes ven solo sus teams
const teamsOptions = computed(() => {
  if (isAdmin.value) {
    return store.getters["teams/getTeams"] || [];
  }
  return store.getters["teams/getMyTeams"] || [];
});

// Cuando admin tiene un agente seleccionado, filtrar solo equipos donde ese agente es miembro.
// Requiere que los miembros de cada equipo estén cargados en el store (teamMembers/get).
const teamOptionsWithAll = computed(() => {
  let options = teamsOptions.value;
  if (isAdmin.value && selectedAgent.value?.id) {
    options = teamsOptions.value.filter((team) => {
      const members = store.getters["teamMembers/getTeamMembers"](team.id);
      return members.some((m) => m.id === selectedAgent.value.id);
    });
  }
  return [
    { id: null, name: t("CONVERSION_FUNNEL.FILTERS.ALL_TEAMS") },
    ...options,
  ];
});

// ── Store ──────────────────────────────────────────────────────────
const isFetching = computed(
  () => store.getters["conversionFunnel/uiFlags"].isFetching,
);
const summary = computed(() => store.getters["conversionFunnel/getSummary"]);

// ── Data fetching ──────────────────────────────────────────────────
const fetchData = () => {
  if (!from.value || !to.value) return;
  store.dispatch("conversionFunnel/fetchSummary", {
    since: from.value,
    until: to.value,
    agentId: selectedAgent.value?.id || undefined,
    teamId: selectedTeam.value?.id || undefined,
    currency: selectedCurrency.value,
    granularity: selectedGranularity.value,
    labelId: selectedLabel.value?.id || undefined,
    labelOperator: selectedLabelOperator.value,
    datePreset: datePreset.value || undefined,
  });
};

const onDateFilterChange = ({ from: newFrom, to: newTo, datePreset: preset }) => {
  from.value = newFrom;
  to.value = newTo;
  const presetChanged = preset !== datePreset.value;
  datePreset.value = preset || null;

  // Los presets de calendario cubren rangos cortos: con granularidad mes o trimestre el
  // gráfico quedaría en una sola barra. El return temprano evita el doble fetch — el
  // watcher de selectedGranularity ya dispara la carga, y para cuando corre (flush 'pre',
  // próximo tick) from / to / datePreset ya están seteados.
  if (preset && presetChanged && selectedGranularity.value !== "day") {
    selectedGranularity.value = "day";
    return;
  }

  fetchData();
};

// Watcher de agente: cuando admins cambian el agente, carga los miembros de todos los equipos
// para que teamOptionsWithAll pueda filtrar correctamente. Si el equipo seleccionado no
// contiene al nuevo agente, lo resetea automáticamente.
watch(selectedAgent, async (newAgent) => {
  if (isAdmin.value) {
    if (newAgent?.id) {
      // Cargar miembros de todos los equipos en paralelo
      await Promise.all(
        teamsOptions.value.map((team) =>
          store.dispatch("teamMembers/get", { teamId: team.id }),
        ),
      );
      // Si el equipo seleccionado no tiene a este agente, resetear
      if (selectedTeam.value?.id) {
        const members = store.getters["teamMembers/getTeamMembers"](
          selectedTeam.value.id,
        );
        if (!members.some((m) => m.id === newAgent.id)) {
          selectedTeam.value = null;
        }
      }
    } else {
      // Volvemos a "Todos los agentes" → limpiar equipo
      selectedTeam.value = null;
    }
  }
  fetchData();
});
watch(selectedTeam, fetchData);
watch(selectedCurrency, fetchData);
watch(selectedGranularity, fetchData);
watch(selectedLabel, fetchData);
// Cambiar el operador sólo recalcula si hay etiqueta seleccionada.
watch(selectedLabelOperator, () => {
  if (selectedLabel.value?.id) fetchData();
});

onMounted(async () => {
  await Promise.all([
    store.dispatch("agents/get"),
    store.dispatch("teams/get"),
    store.dispatch("labels/get"),
  ]);
  // Si es agente (no admin), auto-seleccionar y bloquear el filtro
  if (!isAdmin.value) {
    selectedAgent.value =
      agentsOptions.value.find((a) => a.id === currentUser.value?.id) || null;
  }
});

// ── KPI helpers ────────────────────────────────────────────────────
const deltaPercent = (current, previous) => {
  if (!previous || previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  return { value: Math.abs(pct).toFixed(1), positive: pct >= 0 };
};

const leadsCreated = computed(() => summary.value?.leads_created?.count ?? 0);
const leadsCreatedPrev = computed(
  () => summary.value?.leads_created?.previous ?? 0,
);
const leadsCreatedDelta = computed(() =>
  deltaPercent(leadsCreated.value, leadsCreatedPrev.value),
);

const leadsDerived = computed(() => summary.value?.leads_derived?.count ?? 0);
const leadsDerivedPrev = computed(
  () => summary.value?.leads_derived?.previous ?? 0,
);
const leadsDerivedDelta = computed(() =>
  deltaPercent(leadsDerived.value, leadsDerivedPrev.value),
);

const dealsCreated = computed(() => summary.value?.deals_created?.count ?? 0);
const dealsCreatedPrev = computed(
  () => summary.value?.deals_created?.previous ?? 0,
);
const dealsCreatedDelta = computed(() =>
  deltaPercent(dealsCreated.value, dealsCreatedPrev.value),
);

const dealsWon = computed(() => summary.value?.deals_won?.count ?? 0);
const dealsWonPrev = computed(() => summary.value?.deals_won?.previous ?? 0);
const dealsWonDelta = computed(() =>
  deltaPercent(dealsWon.value, dealsWonPrev.value),
);

const billingAmount = computed(() => summary.value?.billing?.amount ?? 0);
const billingPrev = computed(() => summary.value?.billing?.previous ?? 0);
const billingDelta = computed(() =>
  deltaPercent(billingAmount.value, billingPrev.value),
);

// ── Secondary metrics (rates, velocity, monetization) ──────────────
const dealsLost = computed(() => summary.value?.deals_lost?.count ?? 0);

// Tasa de derivación: % de leads creados que fueron derivados a un agente
const derivedLeadsRate = computed(() => {
  if (!leadsCreated.value) return null;
  return ((leadsDerived.value / leadsCreated.value) * 100).toFixed(1);
});

// Conversión a ganado: % de negocios creados que terminaron ganados
const conversionRate = computed(() => {
  if (!dealsCreated.value) return null;
  return ((dealsWon.value / dealsCreated.value) * 100).toFixed(1);
});

// Tasa de éxito: de los deals cerrados, cuántos ganamos
const winRate = computed(() => {
  const closed = dealsWon.value + dealsLost.value;
  if (!closed) return null;
  return ((dealsWon.value / closed) * 100).toFixed(1);
});

const avgConversionDays = computed(
  () => summary.value?.conversion_time?.avg ?? null,
);
const medianConversionDays = computed(
  () => summary.value?.conversion_time?.median ?? null,
);

// Facturación por lead
// FORK: usa leads_billing (solo deals ganados CON primary_contact) en vez de billing total,
// para que deals sin contacto no distorsionen la ratio. Mismo filtro de etiqueta-deal.
const leadsBillingAmount = computed(
  () => summary.value?.leads_billing?.amount ?? 0,
);
const billingPerLead = computed(() => {
  if (!leadsCreated.value) return null;
  return (leadsBillingAmount.value / leadsCreated.value).toFixed(2);
});

// Facturación por negocio creado
const billingPerDeal = computed(() => {
  if (!dealsCreated.value) return null;
  return (billingAmount.value / dealsCreated.value).toFixed(2);
});

// Ticket promedio: solo deals ganados
const avgTicket = computed(() => {
  if (!dealsWon.value) return null;
  return (billingAmount.value / dealsWon.value).toFixed(2);
});

const formatCurrency = (value) => {
  const formatted = new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
  return `$${formatted}`;
};

const formatCurrencyShort = (value) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formattedBilling = computed(() => formatCurrency(billingAmount.value));
const formattedBillingPrev = computed(() => formatCurrency(billingPrev.value));

// Con un preset de calendario el backend no compara contra "el período anterior" sino contra
// el mismo tramo transcurrido del mes/semana previa, así que el texto tiene que decirlo.
// Las claves van literales (sin interpolar el sufijo) para que el extractor de i18n las vea.
const deltaTooltip = (prevValue, isAmount = false) => {
  if (!prevValue && prevValue !== 0) return "";
  const value = isAmount ? formatCurrency(prevValue) : prevValue;
  if (datePreset.value === "current_month") {
    return t("CONVERSION_FUNNEL.COMPARISON.PREVIOUS_TOOLTIP_MTD", { value });
  }
  if (datePreset.value === "current_week") {
    return t("CONVERSION_FUNNEL.COMPARISON.PREVIOUS_TOOLTIP_WTD", { value });
  }
  return t("CONVERSION_FUNNEL.COMPARISON.PREVIOUS_TOOLTIP", { value });
};

const billingComparisonLabel = computed(() => {
  const value = formattedBillingPrev.value;
  if (datePreset.value === "current_month") {
    return t("CONVERSION_FUNNEL.COMPARISON.VS_PREVIOUS_MTD", { value });
  }
  if (datePreset.value === "current_week") {
    return t("CONVERSION_FUNNEL.COMPARISON.VS_PREVIOUS_WTD", { value });
  }
  return t("CONVERSION_FUNNEL.COMPARISON.VS_PREVIOUS", { value });
});

// ── Sparkline (billing panel) ──────────────────────────────────────
const sparklinePath = computed(() => {
  const data = summary.value?.deals_won_timeseries || [];
  if (data.length < 2) return { line: "", area: "" };
  const values = data.map((p) => p.value);
  const max = Math.max(...values, 1);
  const W = 220;
  const H = 56;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - (v / max) * (H - 4);
    return [x, y];
  });
  const line = "M" + pts.map((p) => p.join(",")).join(" L");
  const area = line + ` L${W},${H} L0,${H} Z`;
  return { line, area };
});

// ── Chart data ─────────────────────────────────────────────────────
// Calcula número de semana ISO (1-53) para una fecha local
const isoWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // Jueves de la semana actual (ISO: semana que contiene el jueves)
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const makeTimestampLabel = (timestamp, gran = selectedGranularity.value) => {
  // El backend envía mediodía UTC: leer componentes UTC para evitar shift TZ
  const d = new Date(timestamp * 1000);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth(); // 0-based
  const day = d.getUTCDate();
  const localDate = new Date(year, month, day);

  switch (gran) {
    case "week": {
      const wn = isoWeekNumber(localDate);
      return `Sem ${String(wn).padStart(2, "0")}, ${year}`;
    }
    case "month":
      return localDate.toLocaleDateString("es-AR", {
        month: "short",
        year: "numeric",
      });
    case "quarter": {
      const q = Math.floor(month / 3) + 1;
      return `Q${q} ${year}`;
    }
    default: // day
      return localDate.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
      });
  }
};

const makeTimeseriesChartData = (timeseries, label, color) => {
  if (!timeseries?.length) return { labels: [], datasets: [] };
  return {
    labels: timeseries.map((p) => makeTimestampLabel(p.timestamp)),
    datasets: [
      {
        label,
        data: timeseries.map((p) => p.value),
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
      },
    ],
  };
};

// ── Mixed chart data helper ────────────────────────────────────────
// Construye el objeto `collection` para un gráfico de barras con línea
// de facturación opcional. Si `showBilling` es false devuelve solo barras.
const makeMixedChartData = (
  mainTs,
  billingTs,
  mainLabel,
  barColor,
  showBilling,
) => {
  if (!mainTs?.length) return { labels: [], datasets: [] };
  const datasets = [
    {
      type: "bar",
      label: mainLabel,
      data: mainTs.map((p) => p.value),
      backgroundColor: barColor,
      borderColor: barColor,
      borderWidth: 1,
      yAxisID: "y",
      order: 2,
    },
  ];
  if (showBilling && billingTs?.length) {
    datasets.push({
      type: "line",
      label: t("CONVERSION_FUNNEL.CHARTS.BILLING_LINE_LABEL"),
      data: billingTs.map((p) => p.value),
      borderColor: "rgba(249, 115, 22, 0.9)",
      backgroundColor: "rgba(249, 115, 22, 0.12)",
      borderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: "rgba(249, 115, 22, 1)",
      fill: false,
      tension: 0.35,
      yAxisID: "y1",
      order: 1,
    });
  }
  return {
    labels: mainTs.map((p) => makeTimestampLabel(p.timestamp)),
    datasets,
  };
};

// ── Billing overlay toggles (uno por gráfico) ──────────────────────
const showBillingLeads = ref(false);
const showBillingDealsCreated = ref(false);
const showBillingDealsWon = ref(true); // default ON en el gráfico de ganados

// ── Chart data ─────────────────────────────────────────────────────
const WOOT_BAR_COLOR = "rgb(31, 147, 255)";

const leadsChartData = computed(() =>
  makeMixedChartData(
    summary.value?.leads_timeseries,
    summary.value?.billing_won_timeseries,
    t("CONVERSION_FUNNEL.CHARTS.LEADS_LABEL"),
    WOOT_BAR_COLOR,
    showBillingLeads.value,
  ),
);

const dealsCreatedChartData = computed(() =>
  makeMixedChartData(
    summary.value?.deals_created_timeseries,
    summary.value?.billing_won_timeseries,
    t("CONVERSION_FUNNEL.CHARTS.DEALS_CREATED_LABEL"),
    WOOT_BAR_COLOR,
    showBillingDealsCreated.value,
  ),
);

const dealsWonChartData = computed(() =>
  makeMixedChartData(
    summary.value?.deals_won_timeseries,
    summary.value?.billing_won_timeseries,
    t("CONVERSION_FUNNEL.CHARTS.DEALS_WON_LABEL"),
    WOOT_BAR_COLOR,
    showBillingDealsWon.value,
  ),
);

const billingByAgentChartData = computed(() => {
  const data = summary.value?.billing_by_agent || [];
  if (!data.length) return { labels: [], datasets: [] };
  return {
    labels: data.map((a) => a.agent_name),
    datasets: [
      {
        label: t("CONVERSION_FUNNEL.CHARTS.BILLING_AGENT_LABEL"),
        data: data.map((a) => a.amount),
        backgroundColor: "rgb(31, 147, 255)",
        borderColor: "rgb(31, 147, 255)",
        borderWidth: 1,
      },
    ],
  };
});

// Plugin inline: dibuja el valor sobre cada barra vertical.
// Usa `afterDraw` para correr FUERA del clip de Chart.js.
// Omite datasets de tipo 'line' para no pintar labels sobre los puntos.
const barValueLabelPlugin = {
  id: "barValueLabels",
  afterDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      // Ignorar datasets que no son barras (p.ej. la línea del gráfico mixto)
      if (dataset.type && dataset.type !== "bar") return;
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden) return;
      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];
        if (!value) return;
        ctx.save();
        ctx.fillStyle = "#475569";
        ctx.font = "600 11px Inter, -apple-system, sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(value, bar.x, bar.y - 3);
        ctx.restore();
      });
    });
  },
};

// Plugin inline para barras HORIZONTALES: dibuja el valor a la derecha de cada barra.
const barValueLabelHorizontalPlugin = {
  id: "barValueLabelsHorizontal",
  afterDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, i) => {
      const meta = chart.getDatasetMeta(i);
      if (meta.hidden) return;
      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];
        if (!value) return;
        ctx.save();
        ctx.fillStyle = "#475569";
        ctx.font = "600 11px Inter, -apple-system, sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(value, bar.x + 5, bar.y);
        ctx.restore();
      });
    });
  },
};

// ── Chart options factory ──────────────────────────────────────────
// Devuelve opciones Chart.js para un gráfico vertical con o sin el eje
// secundario de facturación. Centraliza la lógica para los 3 gráficos.
const makeMixedChartOptions = (showBilling) => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: { padding: { top: 20, right: showBilling ? 16 : 0 } },
  interaction: showBilling ? { mode: "index", intersect: false } : {},
  plugins: {
    legend: {
      display: showBilling,
      position: "top",
      labels: { boxWidth: 12, padding: 16, font: { size: 12 } },
    },
    tooltip: {
      enabled: true,
      callbacks: {
        title: (items) => (items.length ? items[0].label : ""),
        label: (item) => {
          if (!showBilling || item.dataset.type !== "line") {
            return ` ${item.dataset.label}: ${item.raw}`;
          }
          const billing = item.raw;
          const count = item.chart.data.datasets[0].data[item.dataIndex];
          const perUnit =
            count > 0
              ? ` (${formatCurrencyShort(billing / count)} / unidad)`
              : " (—)";
          return ` Facturación: ${formatCurrencyShort(billing)}${perUnit}`;
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      position: "left",
      ticks: { stepSize: 1 },
      ...(showBilling
        ? {
            title: {
              display: true,
              text: "Cantidad",
              font: { size: 11 },
              color: "#64748b",
            },
          }
        : {}),
    },
    ...(showBilling
      ? {
          y1: {
            beginAtZero: true,
            position: "right",
            grid: { drawOnChartArea: false },
            title: {
              display: true,
              text: `Facturación (${selectedCurrency.value})`,
              font: { size: 11 },
              color: "#64748b",
            },
            ticks: { callback: (v) => formatCurrencyShort(v) },
          },
        }
      : {}),
    x: {
      ticks: {
        maxRotation: selectedGranularity.value === "day" ? 45 : 0,
        autoSkip: true,
        maxTicksLimit: selectedGranularity.value === "day" ? 14 : 12,
      },
    },
  },
});

const leadsChartOptions = computed(() =>
  makeMixedChartOptions(showBillingLeads.value),
);
const dealsCreatedChartOptions = computed(() =>
  makeMixedChartOptions(showBillingDealsCreated.value),
);
const dealsWonChartOptions = computed(() =>
  makeMixedChartOptions(showBillingDealsWon.value),
);

const horizontalChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "y",
  layout: { padding: { right: 45 } },
  plugins: { legend: { display: false }, tooltip: { enabled: true } },
  scales: { x: { beginAtZero: true } },
};

const hasLeadsData = computed(
  () => summary.value?.leads_timeseries?.length > 0,
);
const hasDealsCreatedData = computed(
  () => summary.value?.deals_created_timeseries?.length > 0,
);
const hasDealsWonData = computed(
  () => summary.value?.deals_won_timeseries?.length > 0,
);
const hasBillingAgentData = computed(
  () => summary.value?.billing_by_agent?.length > 0,
);
</script>

<template>
  <div class="flex flex-col h-full">
    <ReportHeader
      :header-title="$t('CONVERSION_FUNNEL.HEADER')"
      :header-description="$t('CONVERSION_FUNNEL.DESCRIPTION')"
    />

    <!-- Filters -->
    <div class="flex flex-col gap-3 px-8 pt-4 pb-2">
      <FilterSelector
        :show-agents-filter="false"
        :show-group-by-filter="false"
        :show-business-hours-switch="false"
        @filter-change="onDateFilterChange"
      />

      <!-- Agent and Team selectors (side by side) -->
      <div class="flex items-center gap-3 flex-wrap">
        <!-- Agent selector -->
        <div class="multiselect-wrap--small">
          <multiselect
            v-model="selectedAgent"
            class="no-margin"
            track-by="id"
            label="name"
            :placeholder="$t('CONVERSION_FUNNEL.FILTERS.ALL_AGENTS')"
            :options="
              isAdmin
                ? agentOptionsWithAll
                : agentOptionsWithAll.filter((a) => a.id === currentUser?.id)
            "
            :searchable="isAdmin"
            :allow-empty="false"
            :show-labels="false"
            :disabled="!isAdmin"
          />
        </div>

        <!-- Team selector (for all users) -->
        <div class="multiselect-wrap--small">
          <multiselect
            v-model="selectedTeam"
            class="no-margin"
            track-by="id"
            label="name"
            :placeholder="$t('CONVERSION_FUNNEL.FILTERS.ALL_TEAMS')"
            :options="teamOptionsWithAll"
            :searchable="true"
            :allow-empty="false"
            :show-labels="false"
          />
        </div>

        <!-- FORK: Label selector (todas las etiquetas, cualquier scope) -->
        <div class="multiselect-wrap--small">
          <multiselect
            v-model="selectedLabel"
            class="no-margin"
            track-by="id"
            label="title"
            :placeholder="$t('CONVERSION_FUNNEL.FILTERS.ALL_LABELS')"
            :options="labelOptionsWithAll"
            :searchable="true"
            :allow-empty="false"
            :show-labels="false"
          />
        </div>

        <!-- FORK: operador contiene / no contiene (solo con etiqueta elegida) -->
        <div
          v-if="selectedLabel?.id"
          class="flex items-center gap-1 border border-slate-200 dark:border-slate-600 rounded-lg p-1"
        >
          <button
            v-for="opt in labelOperatorOptions"
            :key="opt.id"
            class="px-3 py-1 text-sm font-medium rounded-md transition-colors"
            :class="
              selectedLabelOperator === opt.id
                ? 'bg-woot-500 text-white'
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
            "
            @click="selectedLabelOperator = opt.id"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <!-- FORK: aviso cuando la etiqueta sólo aplica a negocios -->
      <p
        v-if="showLabelDealNote"
        class="text-xs text-slate-400 dark:text-slate-500"
      >
        {{ $t("CONVERSION_FUNNEL.FILTERS.LABEL_DEAL_NOTE") }}
      </p>

      <div class="flex items-center gap-4 flex-wrap">
        <!-- Currency selector -->
        <div
          class="flex items-center gap-2 border border-slate-200 dark:border-slate-600 rounded-lg p-1"
        >
          <button
            v-for="opt in currencyOptions"
            :key="opt.id"
            class="px-3 py-1 text-sm font-medium rounded-md transition-colors"
            :class="
              selectedCurrency === opt.id
                ? 'bg-woot-500 text-white'
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
            "
            @click="selectedCurrency = opt.id"
          >
            {{ opt.label }}
          </button>
        </div>

        <!-- Granularity selector -->
        <div class="flex items-center gap-2">
          <span class="text-xs text-slate-500 dark:text-slate-400 shrink-0">{{
            $t("CONVERSION_FUNNEL.FILTERS.GRANULARITY_LABEL")
          }}</span>
          <div
            class="flex items-center gap-1 border border-slate-200 dark:border-slate-600 rounded-lg p-1"
          >
            <button
              v-for="opt in granularityOptions"
              :key="opt.id"
              class="px-3 py-1 text-sm font-medium rounded-md transition-colors"
              :class="
                selectedGranularity === opt.id
                  ? 'bg-woot-500 text-white'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              "
              @click="selectedGranularity = opt.id"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading overlay -->
    <LoadingState
      v-if="isFetching"
      :message="$t('CONVERSION_FUNNEL.LOADING')"
    />

    <template v-else-if="summary">
      <!-- KPI Card — 2-column layout -->
      <div class="px-8 pt-4 pb-2">
        <div
          class="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col md:flex-row"
        >
          <!-- LEFT: Facturación destacada (40%) -->
          <div
            class="relative flex flex-col items-center text-center px-8 py-6 md:w-[40%] border-b border-slate-200 dark:border-slate-700 md:border-b-0 md:border-r bg-emerald-50/60 dark:bg-emerald-900/10"
          >
            <!-- Barra acento verde superior -->
            <span
              class="absolute top-0 left-0 right-0 h-[3px] bg-emerald-500 dark:bg-emerald-400"
            />

            <!-- Label + info -->
            <div class="flex items-center justify-center gap-1.5 mt-1 mb-2">
              <span
                class="text-xs font-medium text-slate-500 dark:text-slate-400"
              >
                {{ $t("CONVERSION_FUNNEL.KPIS.BILLING.LABEL") }}
              </span>
              <fluent-icon
                v-tooltip="$t('CONVERSION_FUNNEL.KPIS.BILLING.INFO')"
                icon="info"
                size="13"
                class="text-slate-400 shrink-0 cursor-help"
              />
            </div>

            <!-- Chip de moneda -->
            <span
              class="text-xs font-semibold px-2.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300 leading-none mb-3"
            >
              {{ selectedCurrency }}
            </span>

            <!-- Valor grande en color esmeralda -->
            <span
              class="text-4xl xl:text-5xl font-bold text-emerald-800 dark:text-emerald-300 tabular-nums whitespace-nowrap leading-none"
            >
              {{ formattedBilling }}
            </span>

            <!-- Delta % + vs. período anterior -->
            <div class="mt-2 flex flex-col items-center gap-1">
              <span
                v-if="billingDelta"
                v-tooltip="deltaTooltip(billingPrev, true)"
                class="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-semibold cursor-help leading-none"
                :class="
                  billingDelta.positive
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                    : 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                "
              >
                {{ billingDelta.positive ? "▲" : "▼" }}
                {{ billingDelta.value }}%
              </span>
              <p class="text-xs text-slate-400 dark:text-slate-500">
                {{ billingComparisonLabel }}
              </p>
            </div>

            <!-- Sparkline SVG -->
            <div class="mt-4 w-full">
              <svg
                v-if="sparklinePath.line"
                viewBox="0 0 220 56"
                class="w-full h-14"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path :d="sparklinePath.area" fill="rgba(52,211,153,0.18)" />
                <path
                  :d="sparklinePath.line"
                  fill="none"
                  stroke="rgba(16,185,129,0.7)"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <!-- Placeholder cuando no hay datos -->
              <div v-else class="h-14 flex items-end justify-center opacity-20">
                <svg
                  viewBox="0 0 220 56"
                  class="w-full h-14"
                  aria-hidden="true"
                >
                  <path
                    d="M0,48 Q30,40 55,36 T110,24 T165,16 T220,10"
                    fill="none"
                    stroke="rgb(16,185,129)"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
            </div>
          </div>

          <!-- RIGHT: 4 KPIs en grilla 2x2 (60%) -->
          <div
            class="flex-1 grid grid-cols-2 divide-x divide-y divide-slate-200 dark:divide-slate-700"
          >
            <!-- Leads creados -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center justify-center gap-1.5">
                <span
                  class="text-xs text-slate-500 dark:text-slate-400 leading-snug"
                >
                  {{ $t("CONVERSION_FUNNEL.KPIS.LEADS_CREATED.LABEL") }}
                </span>
                <fluent-icon
                  v-tooltip="$t('CONVERSION_FUNNEL.KPIS.LEADS_CREATED.INFO')"
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold text-slate-900 dark:text-slate-100 tabular-nums"
              >
                {{ leadsCreated }}
              </span>
              <!-- Badge aclaratorio: solo cuando hay agente SIN equipo (leads son globales a pesar del filtro) -->
              <!-- Cuando hay equipo, los leads YA están acotados al equipo, el badge no aplica -->
              <span
                v-if="selectedAgent?.id && !selectedTeam?.id"
                class="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 leading-none"
              >
                {{ $t("CONVERSION_FUNNEL.KPIS.LEADS_CREATED.GLOBAL_BADGE") }}
              </span>
              <span
                v-if="leadsCreatedDelta"
                v-tooltip="deltaTooltip(leadsCreatedPrev)"
                class="inline-flex items-center self-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold cursor-help leading-none"
                :class="
                  leadsCreatedDelta.positive
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                    : 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                "
              >
                {{ leadsCreatedDelta.positive ? "▲" : "▼"
                }}{{ leadsCreatedDelta.value }}%
              </span>
              <span v-else class="h-[20px]" />
            </div>

            <!-- Leads derivados -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center justify-center gap-1.5">
                <span
                  class="text-xs text-slate-500 dark:text-slate-400 leading-snug"
                >
                  {{ $t("CONVERSION_FUNNEL.KPIS.LEADS_DERIVED.LABEL") }}
                </span>
                <fluent-icon
                  v-tooltip="$t('CONVERSION_FUNNEL.KPIS.LEADS_DERIVED.INFO')"
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold text-slate-900 dark:text-slate-100 tabular-nums"
              >
                {{ leadsDerived }}
              </span>
              <span
                v-if="leadsDerivedDelta"
                v-tooltip="deltaTooltip(leadsDerivedPrev)"
                class="inline-flex items-center self-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold cursor-help leading-none"
                :class="
                  leadsDerivedDelta.positive
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                    : 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                "
              >
                {{ leadsDerivedDelta.positive ? "▲" : "▼"
                }}{{ leadsDerivedDelta.value }}%
              </span>
              <span v-else class="h-[20px]" />
            </div>

            <!-- Negocios creados -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center justify-center gap-1.5">
                <span
                  class="text-xs text-slate-500 dark:text-slate-400 leading-snug"
                >
                  {{ $t("CONVERSION_FUNNEL.KPIS.DEALS_CREATED.LABEL") }}
                </span>
                <fluent-icon
                  v-tooltip="$t('CONVERSION_FUNNEL.KPIS.DEALS_CREATED.INFO')"
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold text-slate-900 dark:text-slate-100 tabular-nums"
              >
                {{ dealsCreated }}
              </span>
              <span
                v-if="dealsCreatedDelta"
                v-tooltip="deltaTooltip(dealsCreatedPrev)"
                class="inline-flex items-center self-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold cursor-help leading-none"
                :class="
                  dealsCreatedDelta.positive
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                    : 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                "
              >
                {{ dealsCreatedDelta.positive ? "▲" : "▼"
                }}{{ dealsCreatedDelta.value }}%
              </span>
              <span v-else class="h-[20px]" />
            </div>

            <!-- Negocios ganados -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center justify-center gap-1.5">
                <span
                  class="text-xs text-slate-500 dark:text-slate-400 leading-snug"
                >
                  {{ $t("CONVERSION_FUNNEL.KPIS.DEALS_WON.LABEL") }}
                </span>
                <fluent-icon
                  v-tooltip="$t('CONVERSION_FUNNEL.KPIS.DEALS_WON.INFO')"
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold text-slate-900 dark:text-slate-100 tabular-nums"
              >
                {{ dealsWon }}
              </span>
              <span
                v-if="dealsWonDelta"
                v-tooltip="deltaTooltip(dealsWonPrev)"
                class="inline-flex items-center self-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-semibold cursor-help leading-none"
                :class="
                  dealsWonDelta.positive
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                    : 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                "
              >
                {{ dealsWonDelta.positive ? "▲" : "▼"
                }}{{ dealsWonDelta.value }}%
              </span>
              <span v-else class="h-[20px]" />
            </div>
          </div>
        </div>
      </div>

      <!-- Secondary KPI metrics: rates, velocity, monetization -->
      <div class="px-8 pt-4 pb-2">
        <div
          class="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
        >
          <!-- Fila 1: tasas y velocidad (4 cols) -->
          <div
            class="grid grid-cols-2 md:grid-cols-4 divide-x divide-y divide-slate-100 dark:divide-slate-700 border-b border-slate-100 dark:border-slate-700"
          >
            <!-- Tasa de derivación -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center gap-1.5">
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{
                    $t("CONVERSION_FUNNEL.SECONDARY.DERIVED_LEADS_RATE.LABEL")
                  }}
                </span>
                <fluent-icon
                  v-tooltip="
                    $t('CONVERSION_FUNNEL.SECONDARY.DERIVED_LEADS_RATE.INFO')
                  "
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold tabular-nums"
                :class="
                  derivedLeadsRate !== null
                    ? Number(derivedLeadsRate) >= 40
                      ? 'text-green-700 dark:text-green-400'
                      : Number(derivedLeadsRate) >= 30
                        ? 'text-lime-600 dark:text-lime-400'
                        : Number(derivedLeadsRate) >= 20
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-red-600 dark:text-red-400'
                    : 'text-slate-400'
                "
              >
                {{ derivedLeadsRate !== null ? `${derivedLeadsRate}%` : "—" }}
              </span>
              <span class="text-xs text-slate-400">
                <template v-if="selectedAgent && selectedAgent.id">
                  {{ leadsDerived }}
                  {{
                    $t(
                      "CONVERSION_FUNNEL.SECONDARY.DERIVED_LEADS_RATE.OF_TOTAL",
                    )
                  }}
                  {{ leadsCreated }}
                </template>
                <template v-else>
                  {{ leadsDerived }} / {{ leadsCreated }} leads
                </template>
              </span>
            </div>

            <!-- Conversión a ganado -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center gap-1.5">
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{ $t("CONVERSION_FUNNEL.SECONDARY.CONVERSION_RATE.LABEL") }}
                </span>
                <fluent-icon
                  v-tooltip="
                    $t('CONVERSION_FUNNEL.SECONDARY.CONVERSION_RATE.INFO')
                  "
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold tabular-nums"
                :class="
                  conversionRate !== null
                    ? Number(conversionRate) >= 50
                      ? 'text-green-700 dark:text-green-400'
                      : Number(conversionRate) >= 25
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-red-600 dark:text-red-400'
                    : 'text-slate-400'
                "
              >
                {{ conversionRate !== null ? `${conversionRate}%` : "—" }}
              </span>
              <span class="text-xs text-slate-400">
                {{ dealsWon }} / {{ dealsCreated }} negocios
              </span>
            </div>

            <!-- Tasa de éxito -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center gap-1.5">
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{ $t("CONVERSION_FUNNEL.SECONDARY.WIN_RATE.LABEL") }}
                </span>
                <fluent-icon
                  v-tooltip="$t('CONVERSION_FUNNEL.SECONDARY.WIN_RATE.INFO')"
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold tabular-nums"
                :class="
                  winRate !== null
                    ? Number(winRate) >= 60
                      ? 'text-green-700 dark:text-green-400'
                      : Number(winRate) >= 35
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-red-600 dark:text-red-400'
                    : 'text-slate-400'
                "
              >
                {{ winRate !== null ? `${winRate}%` : "—" }}
              </span>
              <span class="text-xs text-slate-400">
                {{ dealsWon }} gan. / {{ dealsLost }} perd.
              </span>
            </div>

            <!-- Tiempo de conversión -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center gap-1.5">
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{ $t("CONVERSION_FUNNEL.SECONDARY.CONVERSION_TIME.LABEL") }}
                </span>
                <fluent-icon
                  v-tooltip="
                    $t('CONVERSION_FUNNEL.SECONDARY.CONVERSION_TIME.INFO')
                  "
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold text-slate-900 dark:text-slate-100 tabular-nums"
              >
                {{ avgConversionDays !== null ? `${avgConversionDays}d` : "—" }}
              </span>
              <span class="text-xs text-slate-400">
                {{ $t("CONVERSION_FUNNEL.SECONDARY.CONVERSION_TIME.MEDIAN") }}:
                {{
                  medianConversionDays !== null
                    ? `${medianConversionDays}d`
                    : "—"
                }}
              </span>
            </div>
          </div>

          <!-- Fila 2: facturación (3 cols) -->
          <div
            class="grid grid-cols-3 divide-x divide-slate-100 dark:divide-slate-700"
          >
            <!-- Facturación por lead -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center gap-1.5">
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{ $t("CONVERSION_FUNNEL.SECONDARY.BILLING_PER_LEAD.LABEL") }}
                </span>
                <fluent-icon
                  v-tooltip="
                    $t('CONVERSION_FUNNEL.SECONDARY.BILLING_PER_LEAD.INFO')
                  "
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold text-slate-900 dark:text-slate-100 tabular-nums"
              >
                {{
                  billingPerLead !== null
                    ? formatCurrencyShort(Number(billingPerLead))
                    : "—"
                }}
              </span>
              <span class="text-xs text-slate-400">{{ selectedCurrency }}</span>
            </div>

            <!-- Facturación por negocio -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center gap-1.5">
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{ $t("CONVERSION_FUNNEL.SECONDARY.BILLING_PER_DEAL.LABEL") }}
                </span>
                <fluent-icon
                  v-tooltip="
                    $t('CONVERSION_FUNNEL.SECONDARY.BILLING_PER_DEAL.INFO')
                  "
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold text-slate-900 dark:text-slate-100 tabular-nums"
              >
                {{
                  billingPerDeal !== null
                    ? formatCurrencyShort(Number(billingPerDeal))
                    : "—"
                }}
              </span>
              <span class="text-xs text-slate-400">{{ selectedCurrency }}</span>
            </div>

            <!-- Ticket promedio -->
            <div
              class="flex flex-col items-center text-center gap-1.5 px-5 py-5"
            >
              <div class="flex items-center gap-1.5">
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{ $t("CONVERSION_FUNNEL.SECONDARY.AVG_TICKET.LABEL") }}
                </span>
                <fluent-icon
                  v-tooltip="$t('CONVERSION_FUNNEL.SECONDARY.AVG_TICKET.INFO')"
                  icon="info"
                  size="12"
                  class="text-slate-400 shrink-0 cursor-help"
                />
              </div>
              <span
                class="text-2xl leading-none font-bold text-slate-900 dark:text-slate-100 tabular-nums"
              >
                {{
                  avgTicket !== null
                    ? formatCurrencyShort(Number(avgTicket))
                    : "—"
                }}
              </span>
              <span class="text-xs text-slate-400">{{ selectedCurrency }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="flex flex-col gap-6 px-8 py-4">
        <!-- Leads entrantes -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <div class="flex items-center justify-between mb-4">
            <h4
              class="text-sm font-semibold text-slate-800 dark:text-slate-200"
            >
              {{ $t("CONVERSION_FUNNEL.CHARTS.LEADS_TITLE") }}
            </h4>
            <button
              class="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors"
              :class="
                showBillingLeads
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                  : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600'
              "
              @click="showBillingLeads = !showBillingLeads"
            >
              <span aria-hidden="true">📈</span>
              {{
                showBillingLeads
                  ? $t("CONVERSION_FUNNEL.CHARTS.BILLING_HIDE")
                  : $t("CONVERSION_FUNNEL.CHARTS.BILLING_SHOW")
              }}
            </button>
          </div>
          <div class="h-64">
            <BarChart
              v-if="hasLeadsData"
              :collection="leadsChartData"
              :chart-options="leadsChartOptions"
              :plugins="[barValueLabelPlugin]"
            />
            <div
              v-else
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t("CONVERSION_FUNNEL.EMPTY_STATE") }}
            </div>
          </div>
        </div>

        <!-- Negocios creados -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <div class="flex items-center justify-between mb-4">
            <h4
              class="text-sm font-semibold text-slate-800 dark:text-slate-200"
            >
              {{ $t("CONVERSION_FUNNEL.CHARTS.DEALS_CREATED_TITLE") }}
            </h4>
            <button
              class="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors"
              :class="
                showBillingDealsCreated
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                  : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600'
              "
              @click="showBillingDealsCreated = !showBillingDealsCreated"
            >
              <span aria-hidden="true">📈</span>
              {{
                showBillingDealsCreated
                  ? $t("CONVERSION_FUNNEL.CHARTS.BILLING_HIDE")
                  : $t("CONVERSION_FUNNEL.CHARTS.BILLING_SHOW")
              }}
            </button>
          </div>
          <div class="h-64">
            <BarChart
              v-if="hasDealsCreatedData"
              :collection="dealsCreatedChartData"
              :chart-options="dealsCreatedChartOptions"
              :plugins="[barValueLabelPlugin]"
            />
            <div
              v-else
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t("CONVERSION_FUNNEL.EMPTY_STATE") }}
            </div>
          </div>
        </div>

        <!-- Negocios ganados — gráfico mixto barras + línea -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <div class="flex items-center justify-between mb-1">
            <h4
              class="text-sm font-semibold text-slate-800 dark:text-slate-200"
            >
              {{ $t("CONVERSION_FUNNEL.CHARTS.DEALS_WON_TITLE") }}
            </h4>
            <button
              class="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors"
              :class="
                showBillingDealsWon
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                  : 'text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600'
              "
              @click="showBillingDealsWon = !showBillingDealsWon"
            >
              <span aria-hidden="true">📈</span>
              {{
                showBillingDealsWon
                  ? $t("CONVERSION_FUNNEL.CHARTS.BILLING_HIDE")
                  : $t("CONVERSION_FUNNEL.CHARTS.BILLING_SHOW")
              }}
            </button>
          </div>
          <p class="text-xs text-slate-400 dark:text-slate-500 mb-4">
            {{ $t("CONVERSION_FUNNEL.CHARTS.DEALS_WON_SUBTITLE") }}
          </p>
          <div class="h-72">
            <BarChart
              v-if="hasDealsWonData"
              :collection="dealsWonChartData"
              :chart-options="dealsWonChartOptions"
              :plugins="[barValueLabelPlugin]"
            />
            <div
              v-else
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t("CONVERSION_FUNNEL.EMPTY_STATE") }}
            </div>
          </div>
        </div>

        <!-- Facturación por agente -->
        <div
          class="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-6"
        >
          <h4
            class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-1"
          >
            {{ $t("CONVERSION_FUNNEL.CHARTS.BILLING_AGENT_TITLE") }} ·
            {{ selectedCurrency }}
          </h4>
          <p class="text-xs text-slate-400 mb-4">
            {{
              selectedAgent && selectedAgent.id
                ? selectedAgent.name
                : $t("CONVERSION_FUNNEL.FILTERS.ALL_AGENTS")
            }}
          </p>
          <div class="h-72">
            <BarChart
              v-if="hasBillingAgentData"
              :collection="billingByAgentChartData"
              :chart-options="horizontalChartOptions"
              :plugins="[barValueLabelHorizontalPlugin]"
            />
            <div
              v-else
              class="flex items-center justify-center h-full text-sm text-slate-400"
            >
              {{ $t("CONVERSION_FUNNEL.EMPTY_STATE") }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Empty / initial state -->
    <div
      v-else-if="!isFetching"
      class="flex flex-col items-center justify-center py-20 px-8 text-center"
    >
      <fluent-icon
        icon="data-bar-vertical"
        size="48"
        class="text-slate-300 dark:text-slate-600 mb-4"
      />
      <p class="text-sm text-slate-500 dark:text-slate-400">
        {{ $t("CONVERSION_FUNNEL.INITIAL_STATE") }}
      </p>
    </div>
  </div>
</template>
